// Legacy scoring tide functions, pure and Supabase-free (all-sports engine design 2.8 and
// 2.10). Moved verbatim from tide-engine.ts, which re-exports them, so the day engines' revision
// 1 tide port (engines/core/tide-port.ts) and every scorer that imports tide-engine.ts reach no
// database client. Numerically identical to the pre-2026-09-27 formula; revision 2 of each tide
// sport switches to lib/conditions/tide-harmonics.ts through engines/core/tide-track-t.ts.

export type TideConstituent = {
  name: string;
  amplitude: number;
  phase: number;
  speed: number;
};

type Constituent = TideConstituent;

type TidePreferenceWeights = { low: number; mid: number; high: number };

const PREFERENCE_MAP: Record<string, TidePreferenceWeights> = {
  // 'all tides' means tide does not change the session choice at the spot:
  // a spot explicitly configured tide-irrelevant must not be taxed 20% at
  // the extremes while a spot with NO tide data sits at neutral 1.0 (audit
  // 01 V2: data completeness must not decide which spots can score 9+).
  "all tides": { low: 1.00, mid: 1.00, high: 1.00 },
  low: { low: 1.00, mid: 0.93, high: 0.85 },
  mid: { low: 0.85, mid: 1.00, high: 0.85 },
  high: { low: 0.85, mid: 0.93, high: 1.00 },
  "low-mid": { low: 0.97, mid: 1.00, high: 0.85 },
  "mid-high": { low: 0.85, mid: 1.00, high: 0.97 },
};

// Unknown preference is neutral, matching the module's sibling unknown
// defaults (DIR_UNKNOWN_DEFAULT, TIDE_DEFAULT). The old {0.60, 1.00, 0.60}
// was a latent 40%-at-the-extremes tax on the next constituents rollout
// (audit 01 V2, tracker P3-10).
const DEFAULT_WEIGHTS: TidePreferenceWeights = {
  low: 1.00,
  mid: 1.00,
  high: 1.00,
};

export function getTidePreferenceWeights(
  preference: string | null
): TidePreferenceWeights {
  if (!preference) return DEFAULT_WEIGHTS;
  return PREFERENCE_MAP[preference] ?? DEFAULT_WEIGHTS;
}

/*
 * LEGACY SCORING PREDICTOR. Do not use for display or for new scoring.
 *
 * This is the pre-2026-09-27 formula, kept numerically identical so stored
 * and live scores do not move until each sport ships its revision 2: it sums
 * A cos(speed x hoursSince2000 - phase) with no astronomical argument (V0 + u)
 * and no nodal factor (f), so its high water is hours out of phase with the
 * real tide (review SURF-1 / DATA-1), and it normalises by the sum of the
 * amplitudes, so low positions almost never occur. The correct predictor is
 * lib/conditions/tide-harmonics.ts; surf, bodyboarding, prone foil and the
 * paddleboard profiles switch to its day-range position in their per-sport
 * revision 2 (docs/features/tide-engine.md).
 */
function legacyPredictTideLevel(
  constituents: Constituent[],
  timestamp: Date
): number {
  const hours =
    (timestamp.getTime() - Date.UTC(2000, 0, 1)) / (1000 * 60 * 60);
  let level = 0;
  for (const c of constituents) {
    const angle = ((c.speed * hours - c.phase) * Math.PI) / 180;
    level += c.amplitude * Math.cos(angle);
  }
  return level;
}

function legacyTideToNormalised(
  level: number,
  constituents: Constituent[]
): number {
  const maxAmplitude = constituents.reduce(
    (sum, c) => sum + c.amplitude,
    0
  );
  if (maxAmplitude === 0) return 0.5;
  const normalised = (level + maxAmplitude) / (2 * maxAmplitude);
  return Math.max(0, Math.min(1, normalised));
}

function interpolateMultiplier(
  normalisedLevel: number,
  weights: TidePreferenceWeights
): number {
  if (normalisedLevel <= 0.25) {
    const t = normalisedLevel / 0.25;
    return weights.low + t * (weights.mid - weights.low);
  }
  if (normalisedLevel <= 0.75) {
    return weights.mid;
  }
  const t = (normalisedLevel - 0.75) / 0.25;
  return weights.mid + t * (weights.high - weights.mid);
}

/**
 * LEGACY scoring tide position [0, 1] at a timestamp (see the note on
 * legacyPredictTideLevel). Numerically identical to the former
 * computeTidePositionFromConstituents so scoring does not change until the
 * per-sport revision 2 switches to tide-harmonics.ts. Pure.
 */
export function legacyTidePositionForScoring(
  constituents: TideConstituent[],
  timestamp: Date
): number | null {
  if (constituents.length === 0) return null;
  const level = legacyPredictTideLevel(constituents, timestamp);
  return legacyTideToNormalised(level, constituents);
}

/**
 * Pure mapping from a normalised tide position [0, 1] to the per-spot tide
 * multiplier. Null position (no tide model) returns null so callers fall back
 * to the scorer's neutral default. One implementation for every path: the
 * live cron, backfill and the detail page must never re-interpolate the
 * weight tables by hand (audit 01 V2).
 */
export function tideMultiplierFromPosition(
  position: number | null,
  tidePreference: string | null
): number | null {
  if (position === null) return null;
  return interpolateMultiplier(position, getTidePreferenceWeights(tidePreference));
}

/**
 * LEGACY scoring tide positions for one local day, keyed by local hour
 * (formerly buildTidePositionsForDay). Numerically identical to the old
 * formula, which is hours out of phase with the real tide (review SURF-1);
 * surf, bodyboarding, prone foil and the paddleboard profiles keep it until
 * their per-sport revision 2 switches to buildTideDayProfile in
 * tide-harmonics.ts.
 * Returns null when the spot has no tide model or the UTC offset is unknown.
 * Pure: fetch constituents once per spot and reuse.
 */
export function legacyTidePositionsForScoringDay(
  constituents: TideConstituent[],
  dateStr: string,
  utcOffsetSeconds: number | ReadonlyMap<number, number> | null
): Map<number, number> | null {
  if (constituents.length === 0 || utcOffsetSeconds === null) return null;
  const localMidnightAsUtcMs = new Date(`${dateStr}T00:00:00Z`).getTime();
  const positions = new Map<number, number>();
  for (let h = 0; h < 24; h++) {
    const hourOffsetSeconds = typeof utcOffsetSeconds === "number"
      ? utcOffsetSeconds
      : utcOffsetSeconds.get(h);
    if (hourOffsetSeconds === undefined) continue;
    const pos = legacyTidePositionForScoring(
      constituents,
      new Date(
        localMidnightAsUtcMs +
        h * 3_600_000 -
        hourOffsetSeconds * 1000,
      ),
    );
    if (pos !== null) positions.set(h, pos);
  }
  return positions.size > 0 ? positions : null;
}
